/**
 * Capability ladder for the handbook site.
 *
 * Two separate jobs live here:
 *
 *   1. detectTier()        — a one-shot probe at boot. Cheap, synchronous,
 *                            based on what the browser will admit to.
 *   2. QualityGovernor     — a runtime watchdog. Measures real frame times
 *                            and ratchets the tier DOWN if the boot guess was
 *                            optimistic. It never ratchets back up: quality
 *                            that oscillates reads as a broken site, whereas
 *                            quality that quietly settles reads as intentional.
 *
 * Nothing here imports three.js. The renderer subscribes to the tier; the
 * detector knows nothing about the scene. That keeps this testable on a blank
 * page, which is exactly how it is being verified before any 3D exists.
 */

export const TIER = {
  STATIC: 0, // no WebGL, reduced motion, save-data, or software rasterizer
  LOW: 1, // integrated GPU / low memory / most phones
  MID: 2, // capable laptop, high-end phone
  HIGH: 3, // discrete GPU desktop
};

export const TIER_NAME = ['STATIC', 'LOW', 'MID', 'HIGH'];

/**
 * Render settings for each tier. The scene reads these rather than branching
 * on the tier number in a dozen places.
 */
export const TIER_SETTINGS = {
  [TIER.STATIC]: {
    render: false,
    dpr: 1,
    textureTier: 'mid',
    pageSegments: 0,
    bloom: false,
    depthOfField: false,
    shadows: false,
    particles: 0,
  },
  [TIER.LOW]: {
    render: true,
    dpr: 1,
    textureTier: 'lo',
    pageSegments: 12,
    bloom: false,
    depthOfField: false,
    shadows: false,
    particles: 40,
  },
  [TIER.MID]: {
    render: true,
    dpr: 1.5,
    textureTier: 'mid',
    pageSegments: 24,
    bloom: true,
    depthOfField: false,
    shadows: true,
    particles: 180,
  },
  [TIER.HIGH]: {
    render: true,
    dpr: 2,
    textureTier: 'hi',
    pageSegments: 40,
    bloom: true,
    depthOfField: true,
    shadows: true,
    particles: 400,
  },
};

/** Software rasterizers report themselves; they cannot sustain a 3D scene. */
const SOFTWARE_RENDERERS = /swiftshader|llvmpipe|software|basic render|microsoft basic/i;

function probeWebGL() {
  const canvas = document.createElement('canvas');
  const attrs = { failIfMajorPerformanceCaveat: false, powerPreference: 'high-performance' };

  let gl = null;
  let version = 0;
  try {
    gl = canvas.getContext('webgl2', attrs);
    if (gl) version = 2;
    if (!gl) {
      gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs);
      if (gl) version = 1;
    }
  } catch {
    gl = null;
  }
  if (!gl) return { version: 0, renderer: null, maxTextureSize: 0, software: false };

  let renderer = null;
  try {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    // Privacy-hardened browsers hide the real string; UNMASKED_RENDERER_WEBGL
    // returns something generic, which we treat as "unknown", not "bad".
    renderer = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
  } catch {
    renderer = null;
  }

  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0;

  // Release the probe context immediately — browsers cap simultaneous
  // contexts, and losing the real one to a leaked probe is a nasty bug.
  const lose = gl.getExtension('WEBGL_lose_context');
  if (lose) lose.loseContext();

  return {
    version,
    renderer,
    maxTextureSize,
    software: renderer ? SOFTWARE_RENDERERS.test(renderer) : false,
  };
}

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function prefersSaveData() {
  return navigator.connection?.saveData === true;
}

function isSlowConnection() {
  const t = navigator.connection?.effectiveType;
  return t === 'slow-2g' || t === '2g';
}

function isMobileLike() {
  // userAgentData is the honest signal where it exists; the pointer query is
  // the portable fallback. Touch-capable laptops report both, so we require
  // coarse pointer AND no hover to call something a phone/tablet.
  if (navigator.userAgentData?.mobile) return true;
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const noHover = window.matchMedia?.('(hover: none)').matches ?? false;
  return coarse && noHover;
}

/**
 * One-shot boot probe.
 * @returns {{tier:number, reasons:string[], gl:object, cores:number, memory:number, mobile:boolean}}
 */
export function detectTier() {
  const reasons = [];
  const gl = probeWebGL();
  const cores = navigator.hardwareConcurrency || 0; // 0 = undisclosed
  const memory = navigator.deviceMemory || 0; // 0 = undisclosed, Chromium only
  const mobile = isMobileLike();

  // --- hard vetoes: these force STATIC regardless of hardware -------------
  if (prefersReducedMotion()) {
    reasons.push('prefers-reduced-motion: reduce');
    return { tier: TIER.STATIC, reasons, gl, cores, memory, mobile };
  }
  if (prefersSaveData()) {
    reasons.push('Save-Data enabled');
    return { tier: TIER.STATIC, reasons, gl, cores, memory, mobile };
  }
  if (isSlowConnection()) {
    reasons.push(`slow connection (${navigator.connection.effectiveType})`);
    return { tier: TIER.STATIC, reasons, gl, cores, memory, mobile };
  }
  if (gl.version === 0) {
    reasons.push('no WebGL context available');
    return { tier: TIER.STATIC, reasons, gl, cores, memory, mobile };
  }
  if (gl.software) {
    reasons.push(`software rasterizer (${gl.renderer})`);
    return { tier: TIER.STATIC, reasons, gl, cores, memory, mobile };
  }

  // --- scoring ------------------------------------------------------------
  let tier;
  if (cores >= 8 && memory >= 8) {
    tier = TIER.HIGH;
    reasons.push(`${cores} cores / ${memory}GB`);
  } else if (cores >= 4 && (memory >= 4 || memory === 0)) {
    tier = TIER.MID;
    reasons.push(cores ? `${cores} cores / ${memory || '?'}GB` : 'undisclosed hardware');
  } else {
    tier = TIER.LOW;
    reasons.push(`${cores || '?'} cores / ${memory || '?'}GB`);
  }

  // --- caps ---------------------------------------------------------------
  if (gl.version === 1 && tier > TIER.LOW) {
    tier = TIER.LOW;
    reasons.push('WebGL1 only — capped to LOW');
  }
  if (mobile && tier > TIER.MID) {
    // Even a 8-core phone is thermally throttled and pushing a lot of pixels.
    tier = TIER.MID;
    reasons.push('mobile device — capped to MID');
  }
  if (gl.maxTextureSize > 0 && gl.maxTextureSize < 4096 && tier > TIER.LOW) {
    tier = TIER.LOW;
    reasons.push(`max texture size ${gl.maxTextureSize} — capped to LOW`);
  }
  // Safari on iOS has no deviceMemory and lies about very little, but it is
  // also the platform most likely to drop the context under memory pressure.
  if (mobile && memory === 0 && tier > TIER.LOW) {
    tier = TIER.LOW;
    reasons.push('mobile with undisclosed memory — conservative LOW');
  }

  return { tier, reasons, gl, cores, memory, mobile };
}

/**
 * Runtime frame-rate watchdog.
 *
 * Feed it tick(now) from the render loop. It evaluates a rolling window once
 * per second and drops one tier whenever the site has been visibly struggling.
 * Downgrades only.
 */
export class QualityGovernor {
  /**
   * @param {object} opts
   * @param {number} opts.tier            starting tier from detectTier()
   * @param {(tier:number, info:object)=>void} opts.onChange
   * @param {number} [opts.targetFps]     below this counts as struggling
   * @param {number} [opts.panicFps]      below this drops a tier immediately
   * @param {number} [opts.warmupMs]      ignore everything before this
   * @param {number} [opts.strikesNeeded] consecutive bad seconds before a drop
   */
  constructor({
    tier,
    onChange,
    targetFps = 45,
    panicFps = 24,
    warmupMs = 2500,
    strikesNeeded = 2,
  }) {
    this.tier = tier;
    this.onChange = onChange;
    this.targetFps = targetFps;
    this.panicFps = panicFps;
    this.warmupMs = warmupMs;
    this.strikesNeeded = strikesNeeded;

    this.startedAt = performance.now();
    this.windowStart = this.startedAt;
    this.frames = 0;
    this.strikes = 0;
    this.lastFps = 0;
    this.enabled = true;

    // A backgrounded tab stops painting. Without this, alt-tabbing away and
    // back would look like a catastrophic frame drop and demote everyone.
    this._onVisibility = () => this.reset();
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('focus', this._onVisibility);
  }

  /** Discard the current measurement window without penalty. */
  reset() {
    this.windowStart = performance.now();
    this.frames = 0;
    this.strikes = 0;
  }

  /** Call once per rendered frame. */
  tick(now = performance.now()) {
    if (!this.enabled) return;
    this.frames++;

    const elapsed = now - this.windowStart;
    if (elapsed < 1000) return;

    const fps = (this.frames * 1000) / elapsed;
    this.lastFps = fps;
    this.frames = 0;
    this.windowStart = now;

    if (now - this.startedAt < this.warmupMs) return; // shaders still compiling
    if (document.hidden) return;

    if (fps < this.panicFps) {
      this._demote(`panic: ${fps.toFixed(1)} fps`);
      return;
    }
    if (fps < this.targetFps) {
      this.strikes++;
      if (this.strikes >= this.strikesNeeded) {
        this._demote(`${this.strikes} slow seconds, last ${fps.toFixed(1)} fps`);
      }
      return;
    }
    this.strikes = 0;
  }

  _demote(why) {
    this.strikes = 0;
    if (this.tier <= TIER.STATIC) {
      // Already at the floor. Stop measuring; there is nothing left to give.
      this.enabled = false;
      return;
    }
    this.tier -= 1;
    // Give the new tier a fresh warmup before it can be judged again.
    this.startedAt = performance.now();
    this.reset();
    this.onChange?.(this.tier, { why, fps: this.lastFps });
  }

  dispose() {
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('focus', this._onVisibility);
    this.enabled = false;
  }
}

/**
 * Allow forcing a tier for testing: ?tier=0..3 in the URL.
 * Invaluable for checking the STATIC fallback without hunting for a slow phone.
 */
export function tierOverride() {
  const raw = new URLSearchParams(window.location.search).get('tier');
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 3 ? n : null;
}